import { useProject } from "@/common";
import Avatar from "@imminently/decisively-ui/Avatar";
import Icon from "@imminently/decisively-ui/Icon";
import Typography from "@mui/material/Typography";
import { useDispatch } from "react-redux";

import { projectService } from "@/app/providers/services";
import { ImmiCombobox } from "@/components/radix/UserSelect/AsyncSelect";
import { isFeatureEnabled } from "@/config/features";
import { useRouting } from "@/hooks/use-routing";
import { useAddProjectModal } from "@/modals/ProjectModal";
import Button from "@imminently/decisively-ui/Button";
import Select from "@imminently/decisively-ui/Select";
import { useTranslation } from "react-i18next";
import { setProject, useScope } from "../../redux/scope";
import { Skeleton } from "../primitives/skeleton";

export const ProjectSelector = () => {
  const isCompact = isFeatureEnabled("design.compact");
  const dispatch = useDispatch();
  const openAddProject = useAddProjectModal();
  const { goToPage } = useRouting();
  const { workspace, project: projectId } = useScope();
  const project = useProject(projectId);
  const { t } = useTranslation();

  if (!project) return <Skeleton className={"w-60 h-6"} />;

  const comboProps = {
    label: t("projects_label"),
    service: projectService,
    filter: {
      workspace,
      sort: { field: "created", order: "desc" } as const,
    },
    value: project.id,
    onSelect: (item) => dispatch(setProject(item.id, { resetRelease: true })),
    renderItem: (item) => (
      <>
        <Avatar size="md" name={item.name[0]} />
        <span className="block truncate">{item.name}</span>
      </>
    ),
    actions: {
      primary: {
        name: t("browse_all"),
        icon: <Icon name="browse-all" />,
        type: "button" as const,
        variant: "ghost" as const,
        intent: "secondary" as const,
        size: "sm" as const,
        onClick: () => {
          goToPage("/projects");
        },
      },
      secondary: [
        {
          name: t("projects.create_label"),
          icon: <Icon name="add" />,
          type: "button" as const,
          variant: "ghost" as const,
          size: "sm" as const,
          onClick: () => {
            openAddProject();
          },
        },
      ],
    },
  };

  return (
    <ImmiCombobox {...comboProps}>
      {isCompact ? (
        <Button
          label={project?.name ? String(project.name) : t("projects_label")}
          variant="ghost"
          size="sm"
          className="flex items-center gap-2"
        >
          <Icon
            name="data-store"
            className="w-4 h-4"
          />
          <span>{project?.name}</span>
          <Icon
            name="resize"
            className="w-4 h-4"
          />
        </Button>
      ) : (
        <div className="w-60 pointer-events-none">
          <Select
            value={project?.name ? String(project.name) : null}
            options={project?.name ? [{ value: String(project.name), label: String(project.name) }] : []}
            placeholder={t("projects_label")}
            searchable={false}
            menuIsOpen={false}
            openMenuOnClick={false}
            openMenuOnFocus={false}
            onValueChange={() => {}}
            className="w-full text-left"
          />
        </div>
      )}
    </ImmiCombobox>
  );
};
